import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import * as moment from 'moment'
import * as React from 'react'
import { useEffect } from 'react'
import { Caregiver, ECaregiverRelationship, IRiderObject } from '../../../../types'
import UIMessages from '../../../../utils/ui-messages'
import { isNotBlank, isValidEmail } from '../../../../utils/validators'
import ErrorMessage from '../../../ui/ErrorMessage'
import ImageUpload from '../../../ui/ImageUpload'
import CharacterCounter from '../../../ui/CharacterCounter'
import RiderBottomBar from '../EditRiderBottomBar'
import InnerDatePicker from './InnerDatePicker'
import InnerRadioButton from '../../common/InnerRadioButton'
import MaskedPhoneNumberInput from '../../../ui/formik/MaskedPhoneNumberInput'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import * as classNames from 'classnames'
import history from 'src/history'
import { NotificationProvider } from 'src/components/ui/notification-provider'
import { Input, MbscSelectChangeEvent, Select, setOptions, Switch, Textarea } from '@mobiscroll/react-latest'
import '@mobiscroll/react-latest/dist/css/mobiscroll.min.css'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import FormLabel from 'src/components/ui/form/FormLabel'

export interface IFormValues {
  formError?: string
  note?: string
  birthDate?: string
  defaultNotificationPreference: ISelectOption
  email: string
  firstName: string
  lastName: string
  notificationPreference: string
  phone?: string
  countryCode: string
  username: string
  externalId?: string
  internalNote?: string
  orcaSerialNumber?: string
  paymentMode?: string
  //Rider Dark Mode
  darkmode?: boolean
  disableMobileBooking?: boolean
  caregiver?: Caregiver | null
}

interface ISelectOption {
  label: string
  value: string
}

interface IFormProps {
  rider: IRiderObject
}

interface IRiderFormProps {
  rider: IRiderObject
  updateRiderPersonalInfo: (rider: IRiderObject) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

const CHARACTER_LIMIT = 200

const notificationPreferenceOptions: ISelectOption[] = [
  {
    label: UIMessages.common.textMessage,
    value: 'text'
  },
  {
    label: UIMessages.common.voice,
    value: 'voice'
  },
  {
    label: UIMessages.common.none,
    value: 'none'
  }
]
const dropDownTags = [
  { text: '\u{1F91D}Hand-to-Hand', value: `\u{1F91D}Hand-to-Hand` },
  { text: '\u{1F9AE}Service-Animal', value: `\u{1F9AE}Service-Animal` },
  { text: '\u{1F9D1}\u{200D}\u{1F9AF}Blind-Person', value: `\u{1F9D1}\u{200D}\u{1F9AF}Blind-Person` },
  { text: '\u{1F9BB}Hearing-Impaired', value: '\u{1F9BB}Hearing-Impaired' },
  { text: '\u{1F4DE}Call(upon-arrival)', value: '\u{1F4DE}Call(upon-arrival)' },
  { text: '\u{1F9CF}\u{1F919}Cannot-Hear', value: '\u{1F9CF}\u{1F919}Cannot-Hear' },
  { text: '\u{1F697}\u{1F9BD}Putting-Walker-in-trunk', value: '\u{1F697}\u{1F9BD}Putting-Walker-in-trunk' },
  { text: '\u{1F697}\u{1F6E3}Car-close-to-curb', value: '\u{1F697}\u{1F6E3}Car-close-to-curb' },
  { text: '\u{1F697}Car-by-curb-cut/no-curb', value: '\u{1F697}Car-by-curb-cut/no-curb' }
  // { label: 'guide dog', value: '▼・ᴥ・▼ GUIDE DOG' },
  // { label: 'nicer wheel chair', value: '\u{1F469}\u{200D}\u{1F9BC} WHEEL CHAIR' },
  // { label: 'Bed Ridden', value: '\u{1F6CC} BED ridden' },
  // { label: 'wheel chair', value: '♿ WHEEL CHAIR' }
]

const paymentModeList = [
  { text: 'None', value: 'None' },
  { text: 'Cash', value: 'Cash' },
  { text: 'Wallet', value: 'Wallet' }
]

const caregiverList = [
  { text: 'Family', value: ECaregiverRelationship.FAMILY },
  { text: 'Informal', value: ECaregiverRelationship.INFORMAL },
  { text: 'Professional', value: ECaregiverRelationship.PROFESSIONAL },
  { text: 'Volunteer', value: ECaregiverRelationship.VOLUNTEER }
]

setOptions({
  theme: 'windows',
  themeVariant: 'light',
  inputStyle: 'box'
})

const delimeter = '\u2000'
const seperator = ';'
// const seperator = '\u2006'
const re = new RegExp(delimeter, 'g')
export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const {
    errors,
    isSubmitting,
    touched,
    rider,
    values,
    handleBlur,
    setFieldTouched,
    setFieldValue,
    handleSubmit
  } = props
  const [tag, setTagState] = React.useState('')
  const [iPaymentMode, setPaymentModeState] = React.useState('')
  const [typedNote, setTypedNote] = React.useState(seperator)
  const [currentTags, setCurrentTags] = React.useState([''])
  //Rider Dark Mode
  const [darkmode, setDarkmode] = React.useState(values.darkmode)
  const [disableMobileBooking, setDisableMobileBooking] = React.useState(values.disableMobileBooking)
  const notification: any = React.useContext(NotificationProvider)
  const textInput = ({ field }: FieldProps<IFormValues>, label: string) => {
    if ((field.name === 'firstName' && field.value !== '') || (field.name === 'lastName' && field.value !== '')) {
      field.value = field.value.charAt(0).toUpperCase() + field.value.slice(1)
    }
    return (
      <Input
        type='text'
        className='mobiscroll-input form'
        {...field}
        label={label}
        theme='ios'
        themeVariant='light'
        inputStyle='box'
        labelStyle='floating'
      />
    )
  }

  const getCompletePhoneNumber = (countryCode: string, phone: string) => {
    const phoneCountryCode = '+' + countryCode

    let phoneNumber = ''
    if (rider.phone ? rider.phone.indexOf(countryCode) === 0 : false) {
      phoneNumber = phone ? phone.replace(countryCode, '') : ''
    } else {
      phoneNumber = phone ? phone : ''
    }

    return phoneNumber.trim() === '' ? '' : (phoneCountryCode + ' ' + phoneNumber).trim()
  }

  const maskedPhoneInput = ({ field }: FieldProps<IFormValues>) => {
    return <MaskedPhoneNumberInput {...field} />
  }

  const phone = getCompletePhoneNumber(rider.countryCode, rider.phone ? rider.phone : '')
  const caregiverContactNumber = rider.caregiver
    ? getCompletePhoneNumber(
        rider.caregiver.countryCode!,
        rider.caregiver.contactNumber ? rider.caregiver.contactNumber : ''
      )
    : ''

  useEffect(() => {
    handlePhoneField(phone)
    handleCaregiverPhoneField(caregiverContactNumber)
    initializeSharedNote()
  }, [])
  // useEffect(() => {
  //   setFieldValue('note', tag + typedNote)
  // }, [tag])

  const handlePhoneField = (value: any) => {
    setFieldTouched('phone')
    setFieldValue('phone', value)
  }

  const handleCaregiverPhoneField = (value: any) => {
    setFieldTouched('caregiver.contactNumber')
    setFieldValue('caregiver.contactNumber', value)
  }

  //Rider Dark Mode
  const handleDarkmodeToggle = () => {
    setDarkmode(!darkmode)
    setFieldValue('darkmode', !darkmode)
  }

  const handleDisableMobileBookingCheckbox = () => {
    setDisableMobileBooking(!disableMobileBooking)
    setFieldValue('disableMobileBooking', !disableMobileBooking)
  }

  const initializeSharedNote = () => {
    if (values.note && values.note.includes(seperator)) {
      const notes = values.note ? values.note.split(seperator) : ['', seperator]
      const initialTags = notes[0].split(delimeter)
      setTagState(notes[0])
      setTypedNote(seperator + notes[1])
      setCurrentTags(initialTags)
    } else {
      // Date Written: April 12, 2021. remove this later only to clean out the shared note in prod that have no semicolon yet
      setTagState('')
      setTypedNote(seperator + values.note)
      setFieldValue('note', seperator + values.note)
      setCurrentTags([''])
    }
  }
  // to remove the extra tags that appear in the field note, only save the tags that are infront
  const cleanFieldNote = (note: string) => {
    if (note) {
      let fieldNote = note
      dropDownTags.map(item => (fieldNote = fieldNote.split(item.value).join('')))
      const cleanedFieldNote = fieldNote.replace(re, '') // should have same delimeter at reduceString
      setTypedNote(cleanedFieldNote)
      return tag + cleanedFieldNote
    }
    return ''
  }

  const textAreaInput = ({ field }: FieldProps<IFormValues>) => {
    const numOfChar = field.value ? CHARACTER_LIMIT - field.value.length : CHARACTER_LIMIT
    const appendedField = {
      ...field,
      value: cleanFieldNote(field.value)
    }

    return (
      <div className='mt1'>
        <Textarea
          rows={4}
          cols={50}
          placeholder={UIMessages.common.riderProfileNotePlaceHolder}
          maxLength={CHARACTER_LIMIT}
          style={{ resize: 'none' }}
          className='rounded h4 mt1'
          {...appendedField}
          theme='ios'
          themeVariant='light'
          inputStyle='box'
        />
        {touched.note && errors.note ? (
          <span className='font-small weight-300 font-red'>{errors.note}</span>
        ) : (
          <CharacterCounter numOfChar={numOfChar} />
        )}
      </div>
    )
  }

  const textAreaInternalNoteInput = ({ field }: FieldProps<IFormValues>) => {
    const numOfChar = field.value ? CHARACTER_LIMIT - field.value.length : CHARACTER_LIMIT

    return (
      <div className='mt1'>
        <Textarea
          rows={4}
          cols={50}
          placeholder={UIMessages.common.riderProfileInternalNotePlaceHolder}
          maxLength={CHARACTER_LIMIT}
          className='rounded h4 mt1'
          style={{ resize: 'none' }}
          {...field}
          theme='ios'
          themeVariant='light'
          inputStyle='box'
          label='Internal Note'
          labelStyle='floating'
        />
        {touched.internalNote && errors.internalNote ? (
          <span className='font-small weight-300 font-red'>{errors.internalNote}</span>
        ) : (
          <CharacterCounter numOfChar={numOfChar} />
        )}
      </div>
    )
  }

  const reduceString = (stringArr: string[]): string => {
    const tagsArr: string[] = []
    let tagString = ''
    if (stringArr) {
      stringArr.map((item: string) => {
        tagsArr.push(item)
      })
      setCurrentTags(tagsArr)
      tagString = tagsArr.join(delimeter)
    }

    return tagString
  }
  const setTag = (values: MbscSelectChangeEvent) => {
    const tags = reduceString(values.value)
    setTagState(tags)
    setFieldValue('note', tags + typedNote)
  }

  const onChange = (values: MbscSelectChangeEvent) => {
    reduceString(values.value)
  }

  const setPaymentMode = (option: any) => {
    //Set Value
    setFieldValue('paymentMode', option.value)
  }

  const setRiderCaregiver = (option: any) => {
    setFieldValue('caregiver.relationship', option.value)
  }

  const getDefaultPaymentModeValue = () => {
    const paymentMode = paymentModeList.find(option => values.paymentMode === option.value)
    if (paymentMode !== undefined) {
      return paymentMode.text
    } else {
      return ''
    }
  }

  const getRiderCaregiver = () => {
    const relationship = caregiverList.find(
      option => values.caregiver && values.caregiver.relationship === option.value
    )
    if (relationship) {
      return relationship.text
    }
    return ''
  }

  const getNotificationPreference = () => {
    const selected = notificationPreferenceOptions.find(option => option.value === props.values.notificationPreference)

    if (selected) {
      return selected
    } else {
      return notificationPreferenceOptions[2]
    }
  }

  return (
    <Form>
      <ErrorMessage visible={!!errors.formError} className='mt1'>
        {UIMessages.common.failedRiderCreate}
      </ErrorMessage>
      <FloatingForm isSubmitting={isSubmitting} fullHeight={true}>
        <>
          <div className='form-modal-section main-section'>
            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }}>
                {/* Label from ui-messages */}
                <FormLabel>{UIMessages.common.photo}</FormLabel>
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
                <ImageUpload name={rider.firstName + ' ' + rider.lastName} entityId={rider.id} />
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field
                  className='FieldFirstName'
                  name='firstName'
                  render={field => textInput(field, `${UIMessages.common.firstName}*`)}
                />
                {touched.firstName && errors.firstName && (
                  <span className='input-error-msg ErrorMsgDivForFirstName font-small weight-300 font-red'>
                    {errors.firstName}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='email' render={field => textInput(field, `${UIMessages.common.email}`)} />
                {touched.email && errors.email && (
                  <span className='input-error-msg ErrorMsgDivForEmail font-small weight-300 font-red'>
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='lastName' render={field => textInput(field, `${UIMessages.common.lastName}*`)} />
                {touched.lastName && errors.lastName && (
                  <span className='input-error-msg ErrorMsgDivForLastName font-small weight-300 font-red'>
                    {errors.lastName}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} className='ml1 mt1'>
                <PhoneInput
                  className={classNames('phone-input', {
                    'border-red focus-red': !!(touched.phone && errors.phone)
                  })}
                  name='phoneNumber'
                  placeholder='Phone number'
                  defaultCountry='US'
                  value={phone}
                  onChange={handlePhoneField}
                  onBlur={handleBlur}
                />
                {touched.phone && errors.phone && (
                  <span className='font-small weight-300 font-red'>{errors.phone}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }}>
                <Field name='username' render={field => textInput(field, `${UIMessages.common.username}*`)} />
                {touched.username && errors.username && (
                  <span className='font-small weight-300 font-red'>{errors.username}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='ml1 full-width-datepicker ios-datepicker'>
                <div className='datepicker-label'>{UIMessages.common.birthday}</div>
                <InnerDatePicker name={'birthDate'} type='long' label='Birthday' />
                {touched.birthDate && errors.birthDate && (
                  <span className='font-small weight-300 font-red'>{errors.birthDate}</span>
                )}
              </div>
            </div>

            {/* Add Orca Serial Number and Payment Mode */}

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='block mt1'>
                <Field name='orcaSerialNumber' render={field => textInput(field, `${UIMessages.common.orca}`)} />
                {touched.orcaSerialNumber && errors.orcaSerialNumber && (
                  <span className='input-error-msg ErrorMsgDivForEmail font-small weight-300 font-red'>
                    {errors.orcaSerialNumber}
                  </span>
                )}
              </div>

              <div style={{ flex: 1 }} className='block mt1 ml1'>
                <Select
                  data={paymentModeList}
                  value={getDefaultPaymentModeValue()}
                  onChange={setPaymentMode}
                  touchUi={false}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={UIMessages.common.preferredPaymentMethod}
                  labelStyle='floating'
                />

                {touched.paymentMode && errors.paymentMode && (
                  <span className='input-error-msg ErrorMsgDivForEmail font-small weight-300 font-red'>
                    {errors.paymentMode}
                  </span>
                )}
              </div>
            </div>
            {/* Add Orca Serial Number and Payment Mode */}

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Select
                  data={dropDownTags}
                  value={currentTags}
                  onChange={value => setTag(value)}
                  tagInput={true}
                  selectMultiple={true}
                  cssClass='mobiscroll-select'
                  touchUi={false}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={UIMessages.common.riderProfileNote}
                  labelStyle='floating'
                />
                <Field name='note' render={textAreaInput} />
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='internalNote' render={textAreaInternalNoteInput} />
              </div>
            </div>

            <div className='flex'>
              <FormLabel>Caregiver Information</FormLabel>
            </div>
            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field
                  className='FieldFirstName'
                  name='caregiver.firstName'
                  render={field => textInput(field, `${UIMessages.common.firstName}*`)}
                />
                {touched.caregiver && touched.caregiver.firstName && errors.caregiver && errors.caregiver.firstName && (
                  <span className='input-error-msg ErrorMsgDivForFirstName font-small weight-300 font-red'>
                    {errors.caregiver.firstName}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='caregiver.emailAddress' render={field => textInput(field, `${UIMessages.common.email}`)} />
                {touched.caregiver &&
                  touched.caregiver.emailAddress &&
                  errors.caregiver &&
                  errors.caregiver.emailAddress && (
                    <span className='input-error-msg ErrorMsgDivForEmail font-small weight-300 font-red'>
                      {errors.caregiver.emailAddress}
                    </span>
                  )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='caregiver.lastName' render={field => textInput(field, `${UIMessages.common.lastName}*`)} />
                {touched.caregiver && touched.caregiver.lastName && errors.caregiver && errors.caregiver.lastName && (
                  <span className='input-error-msg ErrorMsgDivForLastName font-small weight-300 font-red'>
                    {errors.caregiver.lastName}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} className='ml1 mt1'>
                <PhoneInput
                  className={classNames('phone-input', {
                    'border-red focus-red': !!(
                      touched.caregiver &&
                      touched.caregiver.contactNumber &&
                      errors.caregiver &&
                      errors.caregiver.contactNumber
                    )
                  })}
                  name='caregiver.contactNumber'
                  placeholder='Phone number'
                  defaultCountry='US'
                  value={caregiverContactNumber}
                  onChange={handleCaregiverPhoneField}
                  onBlur={handleBlur}
                />
                {touched.caregiver &&
                  touched.caregiver.contactNumber &&
                  errors.caregiver &&
                  errors.caregiver.contactNumber && (
                    <span className='font-small weight-300 font-red'>{errors.caregiver.contactNumber}</span>
                  )}
              </div>
            </div>

            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }} className='block mt1'>
                <Select
                  data={caregiverList}
                  value={getRiderCaregiver()}
                  onChange={setRiderCaregiver}
                  touchUi={false}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={UIMessages.common.relationship}
                  labelStyle='floating'
                />

                {touched.caregiver &&
                  touched.caregiver.relationship &&
                  errors.caregiver &&
                  errors.caregiver.relationship && (
                    <span className='input-error-msg ErrorMsgDivForEmail font-small weight-300 font-red'>
                      {errors.caregiver.relationship}
                    </span>
                  )}
              </div>
            </div>
          </div>
          <div className='form-modal-section sub-section'>
            <div className='flex'>
              <FormLabel>Mobile Settings</FormLabel>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Dark Mode</FormLabel>
                <Switch checked={darkmode ? darkmode : false} onChange={handleDarkmodeToggle} position='start' />
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Disable Mobile Booking</FormLabel>
                <Switch checked={disableMobileBooking} onChange={handleDisableMobileBookingCheckbox} position='start' />
              </div>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex'>
              <div className='mt1'>
                <div className='pr1 pb1 pt1'>
                  <FormLabel>{UIMessages.common.notificationSetting}</FormLabel>
                  <InnerRadioButton
                    default={getNotificationPreference()}
                    options={notificationPreferenceOptions}
                    name={'notificationPreference'}
                  />
                </div>
              </div>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex'>
              <div className='mt1 w100'>
                <div className='pr1 pb1 pt1'>
                  <Field name='externalId' render={field => textInput(field, `Profile External ID`)} />
                  {touched.externalId && errors.externalId && (
                    <span className='font-small weight-300 font-red'>{errors.externalId}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
      <RiderBottomBar
        cancelPath={`/riders/${rider.id}/profile/info`}
        isSaving={isSubmitting}
        onSave={() => {
          if (!props.isValid) {
            notification.addNotification({
              title: 'Please fix the validation errors before saving.',
              level: 'error'
            })
            return
          }

          notification.addNotification({
            title: 'Personal Info changes were saved successfully.',
            level: 'success'
          })
        }}
      />
    </Form>
  )
}

const validateName = (name: string) => {
  let isValidName = true
  if (/[!@#$%^&*(),.?":{}|<>]/g.test(name) || !/^[a-zA-Z]/.test(name)) {
    isValidName = false
  }
  return isValidName
}

const setCaregiverError = (caregiver: any, msg: string, field: string) => {
  if (!caregiver) {
    return {
      [field]: msg
    }
  }

  return { ...caregiver, [field]: msg }
}

export const PersonalInfoForm = withFormik<IRiderFormProps, IFormValues>({
  mapPropsToValues: (props: IRiderFormProps): IFormValues => {
    const {
      note,
      internalNote,
      birthDate,
      email,
      firstName,
      lastName,
      notificationPreference,
      phone,
      countryCode,
      username,
      orcaSerialNumber,
      // Payment Mode
      paymentMode,
      externalId,
      // Rider Dark Mode
      darkmode,
      disableMobileBooking,
      caregiver
    } = props.rider
    return {
      note,
      internalNote,
      orcaSerialNumber,
      //Payment Mode
      paymentMode,
      birthDate,
      email,
      firstName,
      lastName,
      notificationPreference,
      username,
      externalId,
      phone,
      countryCode,
      // Rider Dark Mode
      darkmode,
      disableMobileBooking,
      caregiver,
      defaultNotificationPreference:
        Object.values(notificationPreferenceOptions).find((it: any) => {
          return it.value === notificationPreference
        }) || notificationPreferenceOptions[2] //Default to none if undefined
    }
  },
  mapValuesToPayload: x => x,
  validate: (
    { email, firstName, lastName, phone, username, birthDate, caregiver }: IFormValues,
    { showMessage }
  ): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}

    if (!isNotBlank(firstName)) {
      errors.firstName = UIMessages.rider.validation.firstNameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    } else if (!validateName(firstName)) {
      errors.firstName = UIMessages.rider.validation.firstNameIsInvalid
      showMessage(UIMessages.rider.validation.invalidName, true)
    }

    if (!isNotBlank(lastName)) {
      errors.lastName = UIMessages.rider.validation.lastNameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    } else if (!validateName(lastName)) {
      errors.lastName = UIMessages.rider.validation.lastNameIsInvalid
      showMessage(UIMessages.rider.validation.invalidName, true)
    }

    if (!isNotBlank(username)) {
      errors.username = UIMessages.rider.validation.usernameIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    }
    if (email && !isValidEmail(email)) {
      errors.email = UIMessages.rider.validation.invalidEmailAddress
      showMessage(UIMessages.rider.validation.invalidEmailAddress, true)
    }
    // if (!isNotBlank(phone ? phone : '')) {
    //   errors.phone = UIMessages.rider.validation.phoneNumberIsRequired
    //   showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    // }
    if (!birthDate || !isNotBlank(birthDate)) {
      errors.birthDate = UIMessages.rider.validation.birthDateIsRequired
      showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
    }

    const phoneNumber = phone ? phone : ''

    if (phoneNumber !== '' && !isValidPhoneNumber(phoneNumber)) {
      errors.phone = UIMessages.rider.validation.phoneNumberInvalid
    }

    if (caregiver) {
      const { emailAddress, contactNumber, countryCode, firstName, lastName, relationship } = caregiver

      if (emailAddress && !isValidEmail(emailAddress)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.invalidEmailAddress,
          'emailAddress'
        )
        showMessage(UIMessages.rider.validation.invalidEmailAddress, true)
      }

      const caregiverPhone = contactNumber ? contactNumber : ''

      if (caregiverPhone !== '' && !isValidPhoneNumber(caregiverPhone)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.phoneNumberInvalid,
          'contactNumber'
        )
        showMessage(UIMessages.rider.validation.phoneNumberInvalid, true)
      }

      if (!isNotBlank(firstName) && isNotBlank(lastName)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.firstNameIsRequired,
          'firstName'
        )
        showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
      } else if (!validateName(firstName) && isNotBlank(firstName)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.firstNameIsInvalid,
          'firstName'
        )
        showMessage(UIMessages.rider.validation.invalidName, true)
      }

      if (!isNotBlank(lastName) && isNotBlank(firstName)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.lastNameIsRequired,
          'lastName'
        )
        showMessage(UIMessages.rider.validation.fillAllRequiredFields, true)
      } else if (!validateName(lastName) && isNotBlank(lastName)) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.lastNameIsInvalid,
          'lastName'
        )
        showMessage(UIMessages.rider.validation.invalidName, true)
      }

      if (isNotBlank(firstName) && isNotBlank(lastName) && (!relationship || !isNotBlank(relationship))) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.caregiverRelationshipIsRequired,
          'relationship'
        )
        showMessage(UIMessages.rider.validation.caregiverRelationshipIsRequired, true)
      }

      if (
        isNotBlank(firstName) &&
        isNotBlank(lastName) &&
        (!isNotBlank(emailAddress || '') && !isNotBlank(caregiverPhone))
      ) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.caregiverContactNumberIsRequired,
          'contactNumber'
        )
        showMessage(UIMessages.rider.validation.caregiverContactNumberIsRequired, true)
      }

      if (
        isNotBlank(firstName) &&
        isNotBlank(lastName) &&
        !isNotBlank(emailAddress || '') &&
        !isNotBlank(caregiverPhone)
      ) {
        errors.caregiver = setCaregiverError(
          errors.caregiver,
          UIMessages.rider.validation.caregiverEmailAddressIsRequired,
          'emailAddress'
        )
        showMessage(UIMessages.rider.validation.caregiverEmailAddressIsRequired, true)
      }
    }

    return errors
  },
  handleSubmit: (
    {
      note,
      birthDate,
      email,
      firstName,
      lastName,
      notificationPreference,
      externalId,
      phone,
      username,
      orcaSerialNumber,
      //Payment Mode
      paymentMode,
      internalNote,
      // Rider Dark Mode
      darkmode,
      disableMobileBooking,
      caregiver
    }: IFormValues,
    { props, setSubmitting }
  ) => {
    setSubmitting(true)
    const phoneInfo = parsePhoneNumber(phone ? phone : '')
    const caregiverContactNumber = parsePhoneNumber(
      caregiver && caregiver.contactNumber !== undefined ? caregiver.contactNumber : ''
    )
    const formattedCaregiver =
      caregiver && !isNotBlank(caregiver.firstName) && !isNotBlank(caregiver.lastName)
        ? null
        : {
            ...caregiver,
            firstName: caregiver ? caregiver.firstName : '',
            lastName: caregiver ? caregiver.lastName : '',
            contactNumber: caregiverContactNumber ? caregiverContactNumber.nationalNumber : '',
            countryCode: caregiverContactNumber ? caregiverContactNumber.countryCallingCode : '',
            relationship: caregiver ? caregiver.relationship : ECaregiverRelationship.FAMILY
          }

    props
      .updateRiderPersonalInfo({
        ...props.rider,
        note,
        internalNote,
        email,
        firstName,
        lastName,
        notificationPreference,
        username,
        orcaSerialNumber: orcaSerialNumber || '0',
        paymentMode,
        externalId,
        birthDate: moment.utc(birthDate).format('MMM DD YYYY'),
        phone: phoneInfo ? phoneInfo.nationalNumber : '',
        countryCode: phoneInfo ? phoneInfo.countryCallingCode : '',
        darkmode,
        disableMobileBooking: disableMobileBooking ? disableMobileBooking : false,
        caregiver: formattedCaregiver
      })
      .then(response => {
        history.push(`/riders/${props.rider.id}/profile/info`)
      })
  }
})(InnerForm)

export default PersonalInfoForm



// WEBPACK FOOTER //
// ./src/components/riders/editrider/personalinfo/PersonalInfoForm.tsx